import React from 'react';
import { css, styled } from '@this/constants/themes';
import { BoxButtonText, Yen } from '../../shared/box_parts';
import SelectTransportBoxPriceRangeLabel from '../price_range_label';
import { media, sizes } from '@this/components/shared/atoms/media';
import { ChangeCancelButtonLabel, OtherCandidatesButtonLabel } from '../../other_candidates_button_label';

type Props = {
  price?: number;
  isAdditional: boolean;
  priceText?: string;
  priceRange?: string;
  showsPricePerPersonText?: boolean;
  showsIncludeTax?: boolean;
  includeTax: boolean;
  selected: boolean;
  disabled?: boolean;
  isRakutenPackage: boolean | null | undefined;
  onClick: () => void;

  // ArrangementRequestForm
  isRequestForm: boolean;
  seatCount?: string | null;
  showsBackground?: boolean;
  alignPriceRight?: boolean;
  priceColor?: string;
  candidateCount: number;
};

const SelectTransportBoxRightInOutlineTemplate: React.FC<Props> = ({
  price,
  isAdditional,
  priceRange,
  showsPricePerPersonText,
  showsIncludeTax,
  includeTax,
  selected,
  disabled,
  onClick,
  isRakutenPackage,
  isRequestForm,
  showsBackground = true,
  alignPriceRight = false,
  priceColor,
  candidateCount
}) => (
  <BoxWrap
    isRakutenPackage={isRakutenPackage === true}
    showsBackground={showsBackground}
    alignPriceRight={alignPriceRight}
  >
    {!isRakutenPackage && (
      <BoxPriceArea alignPriceRight={alignPriceRight}>
        <BoxPriceWrap alignPriceRight={alignPriceRight}>
          <BoxPrice priceColor={priceColor}>
            {price !== undefined && (
              <>
                {isAdditional && <>+ </>}
                <span data-wovn-ignore>{utils.digits(price)}</span>
                <Yen>円</Yen>
              </>
            )}
            {priceRange && <SelectTransportBoxPriceRangeLabel priceRange={priceRange} />}
          </BoxPrice>
          <BoxSmall>
            {showsPricePerPersonText ? '/1名' : ''}
            {((): string => {
              if (!showsIncludeTax) {
                return '';
              }

              if (includeTax) {
                return '(税込)';
              }
              return '(税別)';
            })()}
          </BoxSmall>
        </BoxPriceWrap>
      </BoxPriceArea>
    )}
    {!isRequestForm && (
      <BoxButtonArea alignPriceRight={alignPriceRight}>
        <BoxButton selected={selected} disabled={disabled} onClick={onClick}>
          <BoxButtonText className="select-transport-box__right-gray__button-text">
            {selected ? (
              <ChangeCancelButtonLabel />
            ) : (
              <OtherCandidatesButtonLabel candidateCount={candidateCount} />
            )}
          </BoxButtonText>
        </BoxButton>
      </BoxButtonArea>
    )}
  </BoxWrap>
);

export const BoxWrap = styled.div<{
  isRakutenPackage?: boolean;
  vertical?: boolean;
  showsBackground?: boolean;
  alignPriceRight?: boolean;
}>`
  display: flex;
  flex-direction: ${props => (props.vertical ? 'column' : 'row')};
  width: 100%;
  box-sizing: border-box;
  min-width: 170px;
  padding: ${props => (props.showsBackground === false ? '0 5px 10px 0' : '10px')};
  background: ${props => (props.showsBackground === false ? 'transparent' : props.theme.grayBgColorLight)};
  justify-content: ${props => (props.alignPriceRight ? 'flex-end' : undefined)};
  align-items: ${props => (props.alignPriceRight ? 'center' : undefined)};
  gap: ${props => (props.alignPriceRight ? '8px' : undefined)};

  ${media.sp`
    min-width: auto;
    margin-left: 0;
    width: 100%;
  `}

  @media (max-width: ${sizes.sp}px) {
    padding: ${props => (props.showsBackground === false ? '0 5px 10px 0' : '16px 10px')};
    flex-direction: ${props => (props.showsBackground === false ? 'row' : 'column')};
    align-items: ${props => (props.showsBackground === false ? 'center' : 'stretch')};
    justify-content: ${props => (props.showsBackground === false ? 'flex-end' : 'stretch')};
    gap: ${props => (props.showsBackground === false ? '0' : '8px')};

    ${props =>
      props.showsBackground === false
        ? ''
        : `
      & > *:last-child {
        width: 100%;
        margin: 0;
      }
      & > *:last-child > * {
        width: 100%;
        box-sizing: border-box;
        justify-content: center;
      }
    `}
  }
`;

export const BoxName = styled.div`
  font-size: 12px;
  font-weight: bold;
`;

export const BoxPriceWrap = styled.div<{ alignPriceRight?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: ${props => (props.alignPriceRight ? 'flex-end' : undefined)};
`;

export const BoxPriceArea = styled.div<{ alignPriceRight?: boolean }>`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: ${props => (props.alignPriceRight ? '0' : 'auto 0')};
  flex-shrink: 0;

  @media (max-width: ${sizes.sp}px) {
    width: ${props => (props.alignPriceRight ? 'auto' : '100%')};
    margin: ${props => (props.alignPriceRight ? '0 0 0 auto' : '0')};
    align-self: ${props => (props.alignPriceRight ? 'flex-end' : 'stretch')};
  }
`;

export const BoxPrice = styled.span<{ priceColor?: string }>`
  color: ${props =>
    props.priceColor ?? (props.theme.themeClass === 'tabikobo' ? props.theme.textColor : props.theme.moneyColor)};
  font-size: 18px;
  font-weight: bold;
  display: block;

  ${media.sp`
    line-height: 1;
  `}
`;

export const BoxSmall = styled.div`
  font-size: 12px;
  display: inline-block;

  ${media.sp`
    line-height: 1;
  `}
`;

export const BoxTax = styled.span`
  font-size: 12px;
  display: inline-flex;
`;

export const BoxButtonArea = styled.div<{ alignPriceRight?: boolean }>`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: ${props => (props.alignPriceRight ? '0' : 'auto 0 auto auto')};
  flex-shrink: 0;
`;

export const BoxButton = styled.div<{ selected: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  text-align: center;
  color: ${props => props.theme.linkColor};
  background: white;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  font-weight: bold;
  line-height: 1.2;
  font-size: 13px;
  cursor: pointer;

  ${props =>
    props.selected &&
    `
    background: ${props.theme.linkColor};
    color: white;
  `};

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.6;
          pointer-events: none;
        `
      : null}
`;

export const Area = styled.div`
  color: ${props => props.theme.grayTextColor};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const LoadingArea = styled(Area)`
  padding: 15px 8px 0;
  width: 100%;
  box-sizing: border-box;
`;

export const LoadingAreaText = styled.div`
  padding: 8px 0 3px 0;
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
`;

export const NoVacancyArea = styled(Area)`
  padding-top: 20px;
  max-width: 100px;
`;

export const NoVacancyWithinLimitArea = styled(Area)`
  padding: 20px 3px 0 3px;
  max-width: 125px;
  text-align: center;
`;

export const AreaText = styled.div`
  padding: 8px 0 3px 0;
  position: relative;
  left: 25px;
  white-space: nowrap;
`;

export const NoVacancyText = styled.div`
  font-weight: bold;
  font-size: 13px;
`;

export const SpanBlock = styled.span`
  display: inline-block;
`;

SelectTransportBoxRightInOutlineTemplate.defaultProps = {
  showsPricePerPersonText: true
};

export default SelectTransportBoxRightInOutlineTemplate;
